/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect } from 'react';
import Select, { OptionsOrGroups, StylesConfig, GroupBase } from 'react-select';
import MonsterOpen5e from './model/MonsterOpen5e';
import DataModel from './model/DataModel';
import Monster from './model/Monster';
import CreatureOpen5e from './model/CreatureOpen5e';

interface MonsterOption {
  value: string;
  label: string;
}

interface MonsterSelectorProps {
  onMonsterChange: (monsterId: string | null) => void;
}

interface MonsterOption {
  value: string;
  label: string;
}

interface MonsterSelectorProps {
  onMonsterChange: (monsterId: string | null) => void;
  selectedMonsterId: string | null;
  dataModel: DataModel;
  myMonsters: Monster[];
}

const MonsterSelector: React.FC<MonsterSelectorProps> = ({ onMonsterChange, selectedMonsterId, dataModel, myMonsters }) => {
  const [options, setOptions] = useState<OptionsOrGroups<MonsterOption, GroupBase<MonsterOption>>>([]);
  const [selectedMonster, setSelectedMonster] = useState<MonsterOption | null>(null);
  const [loadProgress, setLoadProgress] = useState<string>('Loading Monster Names...');
  const [, setForceUpdate] = useState(0); // Dummy state to force re-renders

  useEffect(() => {
    // This update just sets a member variable to force a redraw:
    const update = () => {
      setForceUpdate((prev) => prev + 1);
      onUpdateDataModel();
    }
    onUpdateDataModel();

    // Cleanup on unmount
    return dataModel.subscribe(update);
  }, [selectedMonsterId, dataModel, dataModel.monsters, dataModel.monsters.progress, 
                                    dataModel.creatures, dataModel.creatures.progress, myMonsters]);

  const onUpdateDataModel = (() => {
    setLoadProgress(dataModel?.monsters?.progress || dataModel?.creatures?.progress);
    const monsBySource: Map<string, MonsterOption[]> = dataModel.monsters.data.reduce((acc: Map<string, MonsterOption[]>,
                                                                                       monster: MonsterOpen5e) => {
      const key = monster.document__slug;
      if (!acc.has(key)) {
        acc.set(key, []);
      }
      acc.get(key)!.push({ value: monster.slug, label: monster.name });
      return acc;
    }, new Map<string, MonsterOption[]>());

    const creatsBySource: Map<string, MonsterOption[]> = dataModel.creatures.data.reduce((acc: Map<string, MonsterOption[]>,
                                                                                          creature: CreatureOpen5e) => {
      const key = creature.document.key;
      if (!acc.has(key)) {
        acc.set(key, []);
      }
      acc.get(key)!.push({ value: creature.key, label: creature.name });
      return acc;
    }, new Map<string, MonsterOption[]>());

    const sources: Map<string, {name: string; licenses: string[];}> = dataModel.documents;
    const groupCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const formattedOptions: GroupBase<MonsterOption>[] = Array.from(sources.entries(), ([key, license]) => {
      const monsters = [...(monsBySource.get(key) || []), ...(creatsBySource.get(key) || [])];
      return {
        label: license.name,
        options: monsters.sort((m1, m2) => m1.label.localeCompare(m2.label)),
      };
    }).sort((g1, g2) => groupCollator.compare(g1.label, g2.label));
    let newOptions: OptionsOrGroups<MonsterOption, GroupBase<MonsterOption>> = formattedOptions;

    if (myMonsters && myMonsters.length > 0) {
      const myMonsterOptions: MonsterOption[] = myMonsters.map(mon => { return {value: 'user_' + mon.name, label: mon.name}});
      const myMonsterOpts: GroupBase<MonsterOption> = {
        label: 'My Monsters', 
        options: myMonsterOptions.sort((m1: MonsterOption, m2: MonsterOption) => m1.label.localeCompare(m2.label)),
      };
      newOptions = [myMonsterOpts, ...formattedOptions];
    }
    const selectedOption = newOptions.flatMap(groupOrOption =>
                                                'options' in groupOrOption
                                                  ? groupOrOption.options // Extract options if it's a group
                                                  : [groupOrOption]       // Treat it as a single option
                                              )
                                     .find(option => option.value === selectedMonsterId) || null;
    setSelectedMonster(selectedOption);
    setOptions(newOptions);
  });

  const customStyles: StylesConfig<MonsterOption, false, GroupBase<MonsterOption>> = {
    groupHeading: (base) => ({
      ...base,
      backgroundColor: '#f2f2f2',
      color: '#333',
      paddingLeft: '10px',
      padding: '5px',
      fontSize: '26px',
      fontWeight: 'bold',
      textTransform: 'none',
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      paddingLeft: '40px',
      paddingTop: '0px',
      paddingBottom: '0px',
      backgroundColor: isFocused ? 'lightgray' : 'white',
      color: 'black',
      fontWeight: isSelected ? 'bold' : 'normal',
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    control: (provided) => ({ ...provided, width: '60%', minWidth: '350px' }),
  };

  return (
    <Select
      menuPortalTarget={window.document.body}
      styles={customStyles}
      options={options}
      value={selectedMonster}
      onChange={(selectedOption) => {
        setSelectedMonster(selectedOption); // Update local state
        onMonsterChange(selectedOption ? selectedOption.value : null); // Notify parent component
      }}
      filterOption={(option, inputValue) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      }
      id='monster-selector'
      placeholder={loadProgress || 'Select a monster'}
      isClearable
      isSearchable
    />
  );
};

export default MonsterSelector;
